import { useMemo } from "react";

import { SwapInstructionsResponse } from "@loopscale/pandora-types";
import { BalanceChanges, bsMath, lamportsToUiAmount } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { ExecutionDetailsLabels, LoopExecutionDetails } from "./types";

/**
 * Effective USD price of the bought asset: USD notion of the swap input / min output UI.
 * Uses oracle USD for the sell leg only; minOutputAmount defines the bought size (worst case).
 */
export function getExecutionPriceUsdPerBoughtFromQuote({
    quote,
    sellMeta,
    buyMeta,
    sellUsdPrice
}: {
    quote: SwapInstructionsResponse["quote"] | undefined;
    sellMeta: TokenListMetadata | undefined;
    buyMeta: TokenListMetadata | undefined;
    sellUsdPrice: number | undefined;
}): number | undefined {
    if (!quote?.inputMint || !quote.outputMint || !sellMeta || !buyMeta) {
        return undefined;
    }
    if (quote.inputMint !== sellMeta.mint || quote.outputMint !== buyMeta.mint) {
        return undefined;
    }

    const sellUsd = bsMath.mul(lamportsToUiAmount(quote.inputAmount ?? 0, sellMeta.decimals), sellUsdPrice);
    const boughtUi = lamportsToUiAmount(quote.minOutputAmount ?? 0, buyMeta.decimals);
    if (!sellUsd || !(boughtUi > 0)) {
        return undefined;
    }

    return sellUsd / boughtUi;
}

const BASE_EXECUTION_DETAILS_LABELS: Readonly<Required<ExecutionDetailsLabels>> = {
    executionImpact: { caption: "Execution impact", tooltip: undefined, hide: false },
    fairMarketValue: {
        caption: "Fair market value",
        tooltip:
            "What your position is worth based on oracle prices - used for PnL before accounting for execution impact",
        hide: false
    },
    fees: { caption: "Swap fees", tooltip: "Swap fees charged by AMMs", hide: false },
    priceImpact: {
        caption: "Price impact",
        tooltip: "Execution price move due to trade size and pool depth",
        hide: false
    },
    slippage: {
        caption: "Slippage",
        tooltip: "Difference between expected and actual execution price within your slippage tolerance",
        hide: false
    },
    oracleSpread: {
        caption: "Oracle spread",
        tooltip: undefined,
        hide: false
    },
    receiveOnClose: { caption: "Receive on close", tooltip: undefined, hide: false }
};

export function useExecutionDetailsLabels({
    overrides
}: {
    overrides?: Partial<ExecutionDetailsLabels>;
}): Required<ExecutionDetailsLabels> {
    return useMemo(() => {
        const merged: Required<ExecutionDetailsLabels> = JSON.parse(JSON.stringify(BASE_EXECUTION_DETAILS_LABELS));

        for (const k of Object.keys(overrides || {}) as (keyof ExecutionDetailsLabels)[]) {
            const v = (overrides || {})[k];
            if (v) merged[k] = { ...merged[k], ...v };
        }

        return merged;
    }, [overrides]);
}

export function useExecutionDetails({
    slippagePercentDecimals,
    externalQuote,
    receiveUsdAmount,
    fairMarketValue,
    priceImpactAmountOverride,
    tokenBalanceChanges,
    labelOverrides,
    principalUsdPrice,
    collateralUsdPrice,
    principalMetadata,
    collateralMetadata
}: {
    slippagePercentDecimals: number | undefined;
    externalQuote: SwapInstructionsResponse | undefined;
    receiveUsdAmount: number | undefined;
    fairMarketValue: number | undefined;
    priceImpactAmountOverride?: number;
    tokenBalanceChanges?: BalanceChanges | undefined;
    labelOverrides?: Partial<ExecutionDetailsLabels>;
    principalUsdPrice: number | undefined;
    collateralUsdPrice: number | undefined;
    principalMetadata: TokenListMetadata | undefined;
    collateralMetadata: TokenListMetadata | undefined;
}): LoopExecutionDetails | undefined {
    return useMemo(() => {
        if (fairMarketValue === undefined || !receiveUsdAmount) return undefined;

        const feeUsd = (externalQuote?.quote.feeDecimals ?? 0) * receiveUsdAmount;
        const priceImpactUsd =
            priceImpactAmountOverride !== undefined
                ? priceImpactAmountOverride
                : externalQuote?.quote.priceImpactDecimals
                ? externalQuote.quote.priceImpactDecimals * receiveUsdAmount
                : 0;
        const slippageUsd = slippagePercentDecimals ? slippagePercentDecimals * fairMarketValue : 0;

        let tokenChanges = tokenBalanceChanges ?? {};
        if (Object.keys(tokenChanges).length === 0) {
            tokenChanges = {
                [externalQuote?.quote.outputMint ?? ""]: externalQuote?.quote.minOutputAmount ?? 0
            };
        }

        const q = externalQuote?.quote;
        let sellMeta: TokenListMetadata | undefined;
        let buyMeta: TokenListMetadata | undefined;
        let sellUsdPrice: number | undefined;

        if (principalMetadata?.mint === q?.inputMint) {
            sellMeta = principalMetadata;
            sellUsdPrice = principalUsdPrice;
        } else if (collateralMetadata?.mint === q?.inputMint) {
            sellMeta = collateralMetadata;
            sellUsdPrice = collateralUsdPrice;
        }

        if (principalMetadata?.mint === q?.outputMint) {
            buyMeta = principalMetadata;
        } else if (collateralMetadata?.mint === q?.outputMint) {
            buyMeta = collateralMetadata;
        }

        const executionPriceEstimate = getExecutionPriceUsdPerBoughtFromQuote({
            quote: q,
            sellMeta,
            buyMeta,
            sellUsdPrice
        });

        const oraclePriceDeviationUsd = fairMarketValue - receiveUsdAmount - feeUsd - priceImpactUsd - slippageUsd;
        const executionLossUsd = receiveUsdAmount - fairMarketValue;

        // Percent of receive (decimal: 0.01 = 1%). Guard divide-by-zero.
        const executionLossPercent = executionLossUsd / receiveUsdAmount;
        const feePercent = feeUsd / receiveUsdAmount;
        const priceImpactPercent = priceImpactUsd / receiveUsdAmount;
        const slippagePercent = slippageUsd / receiveUsdAmount;
        const oraclePriceDeviationPercent = oraclePriceDeviationUsd / receiveUsdAmount;

        return {
            labelOverrides,
            receiveUsdAmount: receiveUsdAmount,
            fairMarketValueUsd: fairMarketValue,
            executionLossUsd,
            executionLossPercent,
            feeUsd,
            feePercent,
            priceImpactUsd,
            priceImpactPercent,
            slippageUsd,
            slippagePercent,
            oraclePriceDeviationUsd,
            oraclePriceDeviationPercent,
            tokenBalanceChanges: tokenChanges,
            executionPriceEstimate,
            externalQuote
        };
    }, [
        slippagePercentDecimals,
        fairMarketValue,
        receiveUsdAmount,
        externalQuote,
        priceImpactAmountOverride,
        tokenBalanceChanges,
        labelOverrides,
        principalUsdPrice,
        collateralUsdPrice,
        principalMetadata,
        collateralMetadata
    ]);
}
